import type {
	AxiosHeaders,
	AxiosInstance,
	InternalAxiosRequestConfig
} from 'axios'
import axios from 'axios'
import { getHopSkipConfig, protectedResources } from 'lib/auth/auth.config'
import {
	InteractionRequiredAuthError,
	type AccountInfo,
	type AuthenticationResult
} from '@azure/msal-browser'
import msalInstance from 'lib/auth/msal'

const api: AxiosInstance = axios.create({})

let contextStore = {
	userProfileId: undefined as string | undefined,
	impersonatedUserId: undefined as string | undefined
}

export const updateContextValues = (
	userProfileId?: string,
	impersonatedUserId?: string
) => {
	contextStore = {
		userProfileId,
		impersonatedUserId
	}
}

export const getAccessToken = async (): Promise<string | undefined> => {
	await msalInstance.initialize()

	try {
		const accounts: AccountInfo[] = msalInstance.getAllAccounts()
		if (accounts.length === 0) {
			console.warn('No accounts found.')
			await msalInstance.loginRedirect()
			return undefined
		}

		const hopSkipConfig = getHopSkipConfig()
		const account: AccountInfo | undefined = accounts.find(a =>
			a.environment.includes('hopskip')
		)

		if (!account) {
			console.warn('No account found with the specified environment.')
			await msalInstance.loginRedirect()
			return undefined
		}

		const authenticationResult: AuthenticationResult =
			await msalInstance.acquireTokenSilent({
				scopes: protectedResources.apiHopSkip.scopes,
				account,
				redirectUri: `${hopSkipConfig.clientRoot ?? hopSkipConfig.api.host}/auth.html`
			})

		return authenticationResult.accessToken || authenticationResult.idToken
	} catch (error: unknown) {
		if (error instanceof InteractionRequiredAuthError) {
			console.warn('Interaction required, redirecting to login.')
			await msalInstance.logoutRedirect()
		} else if (error instanceof Error) {
			console.error(`Error in getAccessToken: ${error.message}`)
		} else {
			console.error(`Unknown error: ${JSON.stringify(error)}`)
		}
		return undefined
	}
}

export const getServiceHostAndEndpointPath = (
	path: string
): { host: string; endpointPath: string } => {
	const hopSkipConfig = getHopSkipConfig()

	let { host } = hopSkipConfig.api
	const { proxies, hostnamesuffix } = hopSkipConfig.api

	if (!path.startsWith('/api/')) {
		const matches = path.match(/^\/(\w+)\//gi)
		if (matches) {
			const [, service] = matches[0].split('/')
			const proxy = proxies[service]
			host =
				proxy.host ??
				(proxy.local
					? `http://localhost:${proxy.localPort}`
					: `https://${service}${hostnamesuffix}.myhopskip.com`)
			return {
				host,
				endpointPath: `${proxy.includePath ? path : path.replace(matches[0], '/')}${proxy.query ?? ''}`
			}
		}
	}
	return { host, endpointPath: path }
}

api.interceptors.request.use(
	async (
		config: InternalAxiosRequestConfig
	): Promise<InternalAxiosRequestConfig> => {
		const token = await getAccessToken()
		const modifiedConfig: InternalAxiosRequestConfig = {
			...config,
			headers: config.headers
		}

		if (token) {
			;(modifiedConfig.headers as AxiosHeaders).Authorization =
				`Bearer ${token}`
		}

		if (contextStore.impersonatedUserId && contextStore.userProfileId) {
			;(modifiedConfig.headers as AxiosHeaders)['hopskip-userprofile-id'] =
				contextStore.userProfileId
		}

		const { host, endpointPath } = getServiceHostAndEndpointPath(
			config.url ?? ''
		)

		if (host && endpointPath) {
			modifiedConfig.url = `${host}${endpointPath}`
		}

		return modifiedConfig
	}
)

export default api
